import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getRequest } from "@tanstack/react-start/server";
import { campaignEnvironment } from "./email-reactivation-policy";

/**
 * Privacy-safe campaign attribution: standard UTM parameters, landing path and
 * coarse device class only. No click IDs, emails, IPs or study content.
 */
const utmField = z.string().trim().max(100).regex(/^[a-zA-Z0-9][a-zA-Z0-9_\- .]*$/).optional();
const attributionSchema = z.object({
  utmSource: utmField,
  utmMedium: utmField,
  utmCampaign: utmField,
  landingPage: z.string().max(200).regex(/^\/(?!\/)/).optional(),
  deviceType: z.enum(["phone", "tablet", "desktop"]).optional(),
}).optional();

type Attribution = z.infer<typeof attributionSchema>;

function attributionColumns(a: Attribution) {
  if (!a) return {};
  return {
    utm_source: a.utmSource ?? null,
    utm_medium: a.utmMedium ?? null,
    utm_campaign: a.utmCampaign ?? null,
    landing_page: a.landingPage ?? null,
    device_type: a.deviceType ?? null,
  };
}

/** Anonymous funnel signals contain no user identity, study content, IP or advertising identifiers. */
export const recordStudyVisit = createServerFn({ method: "POST" })
  .inputValidator(input => z.object({
    eventId: z.string().uuid(),
    sessionId: z.string().uuid(),
    event: z.enum(["landing_visit", "signup_cta", "registration_started", "signup_attempt"]),
    attribution: attributionSchema,
  }).parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // One signal of each kind per anonymous session; not an authoritative registration or payment.
    const { count, error: readError } = await supabaseAdmin.from("study_conversion_events").select("event_id", { count: "exact", head: true }).eq("session_id", data.sessionId).eq("event_name", data.event);
    if (readError) return { ok: false };
    if (count) return { ok: true };
    const { error } = await supabaseAdmin.from("study_conversion_events").insert({
      event_id: data.eventId,
      session_id: data.sessionId,
      event_name: data.event,
      environment: campaignEnvironment(new URL(getRequest().url).hostname),
      ...attributionColumns(data.attribution),
    });
    return { ok: !error || error.code === "23505" };
  });

export const recordConfirmedRegistration = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(input => z.object({ attribution: attributionSchema }).optional().parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const { data: userData, error } = await context.supabase.auth.getUser();
    const user = userData.user;
    if (error || !user?.confirmed_at) return { ok: false, recorded: false };
    // Returning users must not be reported as newly registered accounts.
    if (Date.now() - new Date(user.created_at).getTime() > 86400000) return { ok: false, recorded: false };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Exactly one confirmed registration per account, even if SIGNED_IN fires
    // repeatedly (token refresh, multiple tabs, OAuth return).
    const { count, error: readError } = await supabaseAdmin.from("study_conversion_events").select("event_id", { count: "exact", head: true }).eq("user_id", context.userId).eq("event_name", "registration_completed");
    if (readError) return { ok: false, recorded: false };
    if (count) return { ok: true, recorded: false };
    const result = await supabaseAdmin.from("study_conversion_events").insert({
      event_id: crypto.randomUUID(),
      user_id: context.userId,
      event_name: "registration_completed",
      environment: campaignEnvironment(new URL(getRequest().url).hostname),
      ...attributionColumns(data?.attribution),
    });
    // `recorded` is true only when this call created the one canonical row —
    // ad-network conversion pixels key off it so unconfirmed signups, returning
    // users and duplicate SIGNED_IN events never fire a signup conversion.
    const recorded = !result.error;
    return { ok: true, recorded };
  });

export const recordStudyTrialOffer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: rate, error: rateError } = await context.supabase.rpc("check_rate_limit", { p_action: "study_trial_offer", p_max_requests: 3, p_window_seconds: 60 });
    if (rateError || !(rate as { allowed?: boolean } | null)?.allowed) return { ok: false };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("study_conversion_events").insert({ event_id: crypto.randomUUID(), user_id: context.userId, event_name: "trial_offer_viewed", environment: campaignEnvironment(new URL(getRequest().url).hostname) });
    return { ok: !error };
  });

export const recordEmailCampaignReturn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(input => z.object({ sessionId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: rate, error: rateError } = await context.supabase.rpc("check_rate_limit", { p_action: "email_campaign_return", p_max_requests: 10, p_window_seconds: 60 });
    if (rateError || !(rate as { allowed?: boolean } | null)?.allowed) return { ok: false };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: recorded, error } = await supabaseAdmin.rpc("record_email_campaign_return", {
      _user_id: context.userId, _session_id: data.sessionId,
      _environment: campaignEnvironment(new URL(getRequest().url).hostname),
    });
    return { ok: !error && recorded === true };
  });
